// src/components/ui/Toast.jsx
//
// App-wide toast/notification system.
//   import { toast } from "../components/ui/Toast";
//   toast.success("Saved");
//   toast.error("Network failed");
//
// ToastContainer is mounted once in App.js so any module can call
// toast(...) without prop-drilling a context.

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

// ── Module-level addToast so any component can call it without
//    needing a context provider in scope. ToastContainer wires this
//    up on mount.
let _addToast = null;

export function toast(message, type = "success", duration = 3000) {
  if (_addToast) {
    _addToast({
      message,
      type,
      duration,
      id: Date.now() + Math.random(),
    });
  } else if (typeof console !== "undefined") {
    // ToastContainer not mounted (very early boot or stripped from a
    // login-only build). Surface to the console so the message isn't
    // silently lost.
    console.warn(`[toast/${type}] ${message}`);
  }
}

toast.success = (msg, dur)  => toast(msg, "success", dur);
toast.error   = (msg, dur)  => toast(msg, "error",   dur || 4000);
toast.info    = (msg, dur)  => toast(msg, "info",    dur);
toast.warning = (msg, dur)  => toast(msg, "warning", dur);

// ── ToastContainer ──
// Mount once in App.js. Renders into <body> via portal so it floats
// above every modal / drawer / panel.
export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((t) => {
    // Cap at 5 visible toasts — oldest gets dropped.
    setToasts((prev) => [...prev.slice(-4), t]);
  }, []);

  useEffect(() => {
    _addToast = addToast;
    return () => { _addToast = null; };
  }, [addToast]);

  const remove = (id) =>
    setToasts((p) => p.filter((t) => t.id !== id));

  if (!toasts.length) return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        pointerEvents: "none",
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={remove} />
      ))}
    </div>,
    document.body
  );
}

function ToastItem({ toast: t, onRemove }) {
  useEffect(() => {
    const timer = setTimeout(() => onRemove(t.id), t.duration || 3000);
    return () => clearTimeout(timer);
  }, [t.id, t.duration, onRemove]);

  const COLORS = {
    success: { border: "rgba(0,255,153,0.25)",  icon: "✓", iconColor: "#00FF99" },
    error:   { border: "rgba(226,75,74,0.25)",  icon: "✕", iconColor: "#E24B4A" },
    warning: { border: "rgba(245,166,35,0.25)", icon: "⚠", iconColor: "#F5A623" },
    info:    { border: "rgba(100,160,255,0.25)",icon: "ℹ", iconColor: "#64A0FF" },
  };
  const c = COLORS[t.type] || COLORS.success;

  return (
    <div
      onClick={() => onRemove(t.id)}
      style={{
        pointerEvents: "auto",
        background: "#0f0f0f",
        border: `0.5px solid ${c.border}`,
        borderLeft: `3px solid ${c.iconColor}`,
        borderRadius: 10,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 260,
        maxWidth: 380,
        cursor: "pointer",
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
        animation: "slideInToast 0.2s ease",
      }}
    >
      <span style={{ fontSize: 14, color: c.iconColor, flexShrink: 0 }}>
        {c.icon}
      </span>
      <span style={{ fontSize: 13, color: "#ccc", lineHeight: 1.4, flex: 1 }}>
        {t.message}
      </span>
    </div>
  );
}

// One-time keyframe injection — guarded so HMR / multiple imports
// don't accumulate duplicates.
if (typeof document !== "undefined" && !document.getElementById("kwery-toast-style")) {
  const styleEl = document.createElement("style");
  styleEl.id = "kwery-toast-style";
  styleEl.textContent = `
    @keyframes slideInToast {
      from { opacity: 0; transform: translateX(20px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(styleEl);
}
